import React, {useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Chip,
    Collapse,
    Divider,
    IconButton,
    Link as MuiLink,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArticleIcon from '@mui/icons-material/Article';
import CampaignIcon from '@mui/icons-material/Campaign';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SummarizeIcon from '@mui/icons-material/Summarize';
import DescriptionIcon from '@mui/icons-material/Description';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import {Link as RouterLink} from 'react-router';
import {getUsersByIds} from '../../../firebase/firestore/users';
import PersonCell, {indexUsersById} from '../../collection/components/PersonCell';
import TabEmpty from './TabEmpty';
import TabLoading from './TabLoading';
import TabPagination from './TabPagination';
import {usePagination} from './usePagination';

const INTERACTION_TYPE_META = {
    fintel: {label: 'FINTEL', icon: ArticleIcon, color: 'success.main'},
    pitch: {label: 'Pitch', icon: CampaignIcon, color: 'info.main'},
    draft: {label: 'Draft', icon: EditNoteIcon, color: 'warning.main'},
    sitrep: {label: 'SITREP', icon: SummarizeIcon, color: 'primary.main'},
    iir: {label: 'IIR', icon: DescriptionIcon, color: 'secondary.main'},
    cm: {label: 'Collected Material', icon: Inventory2OutlinedIcon, color: 'text.secondary'},
    icr: {label: 'ICR', icon: AssignmentIcon, color: 'primary.main'},
    eei: {label: 'EEI', icon: TrackChangesIcon, color: 'info.main'},
    ct: {label: 'Collection Task', icon: TaskAltIcon, color: 'primary.main'},
    note: {label: 'Notes', icon: StickyNote2OutlinedIcon, color: 'text.secondary'},
};

function getInteractionMeta(type) {
    const key = String(type || '').toLowerCase();
    if (INTERACTION_TYPE_META[key]) return INTERACTION_TYPE_META[key];
    const label = key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Other';
    return {label, icon: DescriptionIcon, color: 'text.secondary'};
}

function roleLabel(role) {
    if (!role) return null;
    const s = String(role).replace(/_/g, ' ');
    return s.charAt(0).toUpperCase() + s.slice(1);
}

function interactionTotal(entry) {
    if (typeof entry?.totalCount === 'number') return entry.totalCount;
    const interactions = Array.isArray(entry?.interactions) ? entry.interactions : [];
    return interactions.reduce((sum, g) => sum + (g.count ?? (g.items?.length || 0)), 0);
}

function InteractionItem({item, type, idx}) {
    const title = item.title || item.id || 'Untitled';

    if (item.href) {
        return (
            <MuiLink
                component={RouterLink}
                to={item.href}
                underline="hover"
                variant="body2"
                color="text.primary"
                sx={{
                    display: 'block',
                    py: 0.25,
                    '&:hover': {color: 'primary.main'},
                }}
            >
                {title}
            </MuiLink>
        );
    }

    return (
        <Typography variant="body2" color="text.secondary" sx={{py: 0.25}}>
            {title}
        </Typography>
    );
}

function InteractionSection({group}) {
    const items = Array.isArray(group?.items) ? group.items : [];
    const count = group?.count ?? items.length;
    const type = group?.type;
    const {label, icon: Icon, color} = getInteractionMeta(type);

    if (count === 0) return null;

    return (
        <Box>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.75}>
                <Icon sx={{fontSize: 18, color, flexShrink: 0}}/>
                <Typography variant="subtitle2" fontWeight={600} component="span">
                    {label}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="span">
                    {count}
                </Typography>
            </Stack>
            <Stack spacing={0.25} pl={3.5}>
                {items.map((item, idx) => (
                    <InteractionItem key={item.id || item.href || `${type}-${idx}`} item={item} type={type} idx={idx}/>
                ))}
            </Stack>
        </Box>
    );
}

function InteractionPreviewChips({interactions}) {
    const chips = (Array.isArray(interactions) ? interactions : [])
        .filter((g) => (g.count ?? g.items?.length ?? 0) > 0)
        .slice(0, 4);

    if (!chips.length) return null;

    return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{maxWidth: 280}}>
            {chips.map((group, idx) => {
                const count = group.count ?? group.items?.length ?? 0;
                const {label} = getInteractionMeta(group.type);
                return (
                    <Chip
                        key={`${group.type || 'x'}-${idx}`}
                        size="small"
                        variant="outlined"
                        label={`${label} · ${count}`}
                        sx={{
                            height: 22,
                            '& .MuiChip-label': {px: 0.75, fontSize: '0.68rem'},
                        }}
                    />
                );
            })}
        </Stack>
    );
}

function PersonnelRow({entry, profile, defaultExpanded = false}) {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const userId = entry.userId;
    const roles = Array.isArray(entry.roles) ? entry.roles : [];
    const interactions = Array.isArray(entry.interactions) ? entry.interactions : [];
    const totalCount = interactionTotal(entry);

    return (
        <Paper
            variant="outlined"
            sx={{
                overflow: 'hidden',
                transition: 'box-shadow 0.15s ease',
                ...(expanded ? {boxShadow: 1} : {}),
            }}
        >
            <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                onClick={() => setExpanded((v) => !v)}
                sx={{
                    px: 1.5,
                    py: 1.25,
                    cursor: 'pointer',
                    '&:hover': {bgcolor: 'action.hover'},
                }}
            >
                <Box sx={{minWidth: 0, flexShrink: 0}}>
                    <PersonCell
                        userId={userId}
                        profile={profile || entry.profile || entry.user || null}
                    />
                </Box>
                <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{minWidth: 0}}
                >
                    {roles.map((role) => (
                        <Chip
                            key={role}
                            size="small"
                            label={roleLabel(role)}
                            variant="outlined"
                            sx={{
                                textTransform: 'capitalize',
                                height: 22,
                                '& .MuiChip-label': {px: 0.75, fontSize: '0.68rem'},
                            }}
                        />
                    ))}
                </Stack>
                {!expanded ? (
                    <Box sx={{ml: 'auto', display: {xs: 'none', sm: 'block'}}}>
                        <InteractionPreviewChips interactions={interactions}/>
                    </Box>
                ) : null}
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ml: expanded ? 'auto' : 0, whiteSpace: 'nowrap', flexShrink: 0}}
                >
                    {totalCount} interaction{totalCount === 1 ? '' : 's'}
                </Typography>
                <IconButton
                    size="small"
                    aria-expanded={expanded}
                    aria-label={expanded ? 'Collapse interactions' : 'Expand interactions'}
                    onClick={(e) => {
                        e.stopPropagation();
                        setExpanded((v) => !v);
                    }}
                    sx={{
                        flexShrink: 0,
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                    }}
                >
                    <ExpandMoreIcon fontSize="small"/>
                </IconButton>
            </Stack>

            {!expanded ? (
                <Box sx={{display: {xs: 'block', sm: 'none'}, px: 1.5, pb: 1}}>
                    <InteractionPreviewChips interactions={interactions}/>
                </Box>
            ) : null}

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Divider/>
                <Box sx={{px: 2, py: 1.75, bgcolor: 'grey.50'}}>
                    {interactions.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No interactions listed.
                        </Typography>
                    ) : (
                        <Stack spacing={2}>
                            {interactions.map((group, idx) => (
                                <InteractionSection
                                    key={`${group.type || 'x'}-${idx}`}
                                    group={group}
                                />
                            ))}
                        </Stack>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
}

/**
 * Personnel tab — people who interacted with this entity via reports,
 * Collected Materials, ICR/EEI/Collection Tasks, and Notes.
 *
 * @param {Object} props
 * @param {Array} props.personnel
 * @param {boolean} props.loading
 * @param {Error|null} [props.error]
 */
const PersonnelTab = ({personnel, loading, error = null}) => {
    const [people, setPeople] = useState({});

    const sorted = useMemo(() => {
        const list = Array.isArray(personnel) ? [...personnel] : [];
        // Match EntityPersonnelService: totalCount DESC, then userId.
        list.sort((a, b) => {
            const ta = interactionTotal(a);
            const tb = interactionTotal(b);
            if (tb !== ta) return tb - ta;
            return String(a.userId || '').localeCompare(String(b.userId || ''));
        });
        return list;
    }, [personnel]);

    const userIdsKey = useMemo(
        () => sorted.map((e) => e.userId).filter(Boolean).join(','),
        [sorted],
    );

    useEffect(() => {
        let cancelled = false;
        const ids = userIdsKey ? userIdsKey.split(',') : [];
        if (!ids.length) {
            setPeople({});
            return undefined;
        }
        getUsersByIds(ids)
            .then((users) => {
                if (!cancelled) setPeople(indexUsersById(users));
            })
            .catch(() => {
                if (!cancelled) setPeople({});
            });
        return () => {
            cancelled = true;
        };
    }, [userIdsKey]);

    const {page, setPage, pageItems, totalPages, hasPagination} = usePagination(sorted);

    if (loading) return <TabLoading/>;

    if (error) {
        return (
            <Box p={2}>
                <Alert severity="error">{error.message || 'Failed to load personnel.'}</Alert>
            </Box>
        );
    }

    if (!sorted.length) {
        return <TabEmpty message="No personnel interactions for this entity."/>;
    }

    return (
        <Stack>
            <Stack spacing={1.5} px={2} pt={2}>
                {pageItems.map((entry, idx) => (
                    <PersonnelRow
                        key={entry.userId || entry.id}
                        entry={entry}
                        profile={people[entry.userId] || null}
                        defaultExpanded={page === 1 && idx === 0}
                    />
                ))}
            </Stack>
            <TabPagination
                hasPagination={hasPagination}
                page={page}
                totalPages={totalPages}
                totalItems={sorted.length}
                onChange={setPage}
            />
        </Stack>
    );
};

export default PersonnelTab;
